// Weft shared message protocol — standardized event envelope.
//
// Two layers:
//  1. TransportMessage — the opaque, E2E-encrypted envelope that travels over the relay.
//     Shape: { channelId, event, iv, ciphertext, ts }. The relay never sees plaintext. The
//     transport-level `event` is ALWAYS the message's `eventType` (see EVENT_TYPE below).
//  2. EventEnvelope — the decrypted, standardized object every message uses:
//
//       { eventType, eventSubtype, channelId, sessionId, senderId, senderName, msg, ts }
//
//     `eventType`/`eventSubtype` classify the message; `msg` carries EVERYTHING type-specific
//     (never flattened onto the envelope). Identity fields (channelId/sessionId/senderId/
//     senderName) are stamped by SecureChannel (channel.mjs) on send, so factories only build
//     { eventType, eventSubtype, msg, ts } and the channel fills in who/where.
//
// Zero dependencies on purpose: importable as-is by the Node extension and the browser app.

/**
 * Top-level event types. Each is ALSO the transport topic the message publishes/subscribes on,
 * so there is no separate kind->event lookup to keep in sync — `eventType` is just a field.
 */
export const EVENT_TYPE = Object.freeze({
  STREAM: "stream", // ext -> phone: assistant tokens, tool activity, logs, activity, user echo
  PROMPT: "prompt", // phone -> ext: user prompt
  APPROVAL: "approval", // ext -> phone: native permission request
  DECISION: "decision", // phone -> ext: user's choice for an approval
  ELICITATION: "elicitation", // ext -> phone: ask_user form request + its completion (dismiss)
  ELICITATION_RESPONSE: "elicitation_response", // phone -> ext: the user's answer to a form
  CONTROL: "control", // both: lifecycle, heartbeat, mode, history, state
  PAIR: "pair", // both (plaintext, pre-key): the ECDH handshake (hello/ack)
});

export const DEVICE_CAPABILITY = Object.freeze({
  MONITOR_V1: "device-monitor-v1",
  TERMINAL_V1: "device-terminal-v1",
  CLIPBOARD_V1: "device-clipboard-v1",
  KEEP_AWAKE_V1: "device-keep-awake-v1",
  SESSION_ACTIVATION_V1: "session-activation-v1",
});

export const SESSION_ACTIVATION_CAPABILITY = DEVICE_CAPABILITY.SESSION_ACTIVATION_V1;
export const LIFECYCLE_RETENTION_MS = 3 * 24 * 60 * 60 * 1000;
export const LIFECYCLE_MESSAGE_MAX_BYTES = 64 * 1024;
export const CLIPBOARD_MAX_BYTES = 64 * 1024;
export const KEEP_AWAKE_MIN_MS = 15 * 60 * 1000;
export const KEEP_AWAKE_MAX_MS = 8 * 60 * 60 * 1000;
export const DEVICE_UTILITY_CODES = Object.freeze([
  "ok", "invalid-request", "too-large", "unsupported", "unavailable", "timeout", "lease-mismatch",
]);

/** Shared by both endpoints; never include the rejected text in errors. */
export function clipboardTextError(text) {
  if (typeof text !== "string") return "invalid-request";
  return new TextEncoder().encode(text).byteLength > CLIPBOARD_MAX_BYTES ? "too-large" : null;
}

export function normalizeKeepAwakeDuration(durationMs) {
  return Number.isFinite(durationMs) && durationMs > 0
    ? Math.max(KEEP_AWAKE_MIN_MS, Math.min(KEEP_AWAKE_MAX_MS, Math.floor(durationMs)))
    : null;
}

/**
 * Fine-grained subtype, scoped UNDER its eventType (so the same subtype string may appear under
 * different types, e.g. `request` under both APPROVAL and ELICITATION). A message is uniquely
 * identified by the (eventType, eventSubtype) pair.
 */
export const SUBTYPE = Object.freeze({
  STREAM: Object.freeze({
    ASSISTANT_MESSAGE: "assistant_message",
    ASSISTANT_DELTA: "assistant_delta",
    // one-line "what I'm doing right now" from the agent, plus whether it is mid-thought. Ephemeral
    // by design: the SDK never persists either signal, so this is only ever a live hint and is
    // deliberately absent from replayed history.
    INTENT: "intent",
    TOOL_START: "tool_start",
    TOOL_COMPLETE: "tool_complete",
    LOG: "log",
    // turn-level activity: true while the agent is generating/acting, false when idle.
    ACTIVITY: "activity",
    // a user prompt typed at the laptop terminal, echoed to the phone (origin distinguishes source).
    USER_MESSAGE: "user_message",
  }),
  PROMPT: Object.freeze({ PROMPT: "prompt" }),
  APPROVAL: Object.freeze({ REQUEST: "request", COMPLETE: "complete" }),
  DECISION: Object.freeze({ APPROVAL_DECISION: "approval_decision" }),
  ELICITATION: Object.freeze({ REQUEST: "request", COMPLETE: "complete" }),
  ELICITATION_RESPONSE: Object.freeze({ RESPONSE: "response" }),
  CONTROL: Object.freeze({
    CHANNEL_UP: "channel_up",
    WORKSPACE_REQUEST: "workspace_request",
    WORKSPACE_RESULT: "workspace_result",
    SESSION_META: "session_meta",
    CHANNEL_DOWN: "channel_down",
    HEARTBEAT: "heartbeat",
    MODE: "mode",
    INTERRUPT: "interrupt",
    HISTORY_REQUEST: "history_request",
    HISTORY: "history",
    // In-memory recent-turns snapshot (higher fidelity than the DB: captures the full assistant
    // text the CLI store drops for long/multi-tool turns). Requested on connect; see recentTurns().
    RECENT_TURNS_REQUEST: "recent_turns_request",
    RECENT_TURNS: "recent_turns",
    STATE_REQUEST: "state_request",
    STATE_SNAPSHOT: "state_snapshot",
    // --- phone-launched sessions (#156): talk to a `weft` listener over its paired channel ---
    // phone -> listener: give me your registered projects (sent right after pairing).
    PROJECT_LIST_REQUEST: "project_list_request",
    // listener -> phone: the machine's registered projects + the listener's display name.
    PROJECT_LIST: "project_list",
    // phone -> listener: spawn a new Copilot session for a project with a permission mode.
    SPAWN_SESSION: "spawn_session",
    // --- phone-driven resume of an existing CLI session (#resume) -----------
    // phone -> listener: give me the machine's recent resumable Copilot sessions (from the CLI
    // session store), so the phone can offer a "Resume a session" list. On-demand only (viewed
    // occasionally, and the store is large + high-churn) — never pushed on bind like PROJECT_LIST.
    SESSION_LIST_REQUEST: "session_list_request",
    // listener -> phone: the machine's recent resumable sessions (newest-first, capped), each
    // `{ sessionId, title, cwd, repository, branch, updatedAt }`. Sessions whose cwd no longer
    // exists are filtered out (a resume needs a valid working directory).
    SESSION_LIST: "session_list",
    // phone -> listener: resume an existing CLI session by id (spawns `copilot --resume=<id>` in
    // that session's own cwd). Replies over the SAME SPAWN_PAIRING / SPAWN_RESULT path a fresh
    // spawn uses, so the phone pairs to the resumed session digitally (no QR).
    RESUME_SESSION: "resume_session",
    // Additive session-activation-v1 lifecycle contract. Mixed-version peers can project these
    // messages to and from the legacy spawn/resume/status messages below.
    OPEN_SESSION: "open_session",
    LIFECYCLE_STATUS_REQUEST: "lifecycle_status_request",
    LIFECYCLE_STATUS: "lifecycle_status",
    TAKEOVER_CONFIRM: "takeover_confirm",
    LIFECYCLE_CANCEL: "lifecycle_cancel",
    // listener -> phone: the pre-minted pairing payload for a freshly spawned session.
    SPAWN_PAIRING: "spawn_pairing",
    // listener -> phone: terminal result of a spawn request (ok / failure reason).
    SPAWN_RESULT: "spawn_result",
    // listener -> phone: durable lifecycle state for a launch request. Additive to the legacy
    // SPAWN_PAIRING / SPAWN_RESULT replies so older phones continue to work unchanged.
    LAUNCH_STATUS: "launch_status",
    // phone -> listener: forget this device (the listener stops / drops the binding).
    FORGET_DEVICE: "forget_device",
    // listener -> phone: proactive liveness beat for the DEVICE (not a session) channel, sent on an
    // interval independent of PROJECT_LIST request/reply. Lets the phone detect a dead/hung listener
    // process even though the underlying transport still reports "connected" (see deviceHeartbeat()).
    DEVICE_HEARTBEAT: "device_heartbeat",
    DEVICE_MONITOR_START: "device_monitor_start",
    DEVICE_MONITOR_STOP: "device_monitor_stop",
    DEVICE_SNAPSHOT: "device_snapshot",
    TERMINAL_REQUEST: "terminal_request",
    TERMINAL_STATE: "terminal_state",
    TERMINAL_OUTPUT: "terminal_output",
    TERMINAL_SNAPSHOT: "terminal_snapshot",
    CLIPBOARD_READ: "clipboard_read",
    CLIPBOARD_WRITE: "clipboard_write",
    CLIPBOARD_RESULT: "clipboard_result",
    KEEP_AWAKE_START: "keep_awake_start",
    KEEP_AWAKE_STOP: "keep_awake_stop",
    KEEP_AWAKE_STATUS_REQUEST: "keep_awake_status_request",
    KEEP_AWAKE_STATUS: "keep_awake_status",
    // phone -> ext: Voice Mode (#168) is on/off. While on, the extension prepends a directive to
    // each relayed prompt so the agent authors its reply for SPEECH (concise, no verbatim code).
    VOICE_MODE: "voice_mode",
    // phone -> ext: invoke a whitelisted Copilot CLI slash command on the laptop session
    // (session.rpc.commands.invoke). The set of allowed commands lives in commands.mjs; the
    // extension re-validates against it before running. { name, input? }.
    INVOKE_COMMAND: "invoke_command",
    // --- laptop-initiated /weft sessions offered through a running station (#offer) ---
    // listener -> phone: the set of in-session `/weft` sessions currently waiting to be adopted by
    // the paired phone (each carries its own SESSION pairing payload). Sent right after PROJECT_LIST
    // on (re)connect and again whenever the on-disk pending set changes. The mirror image of
    // SPAWN_PAIRING: there the phone asks the station to spawn a session; here a session the user
    // started at the terminal advertises itself to the already-paired phone (see sessionOffers()).
    SESSION_OFFERS: "session_offers",
    // phone -> listener: the phone has adopted (paired to) an offered session, so the station can
    // drop it from its advertised set. Idempotent; carries the offered session's channelId.
    SESSION_CLAIMED: "session_claimed",
  }),
  PAIR: Object.freeze({ HELLO: "hello", CHALLENGE: "challenge", PROOF: "proof", ACK: "ack" }),
});

/** Session modes the phone can request. (Applied best-effort by the extension; see spike.) */
export const MODES = Object.freeze(["interactive", "plan", "autopilot"]);
export const LAUNCH_STATES = Object.freeze([
  "accepted",
  "launched",
  "ready",
  "failed",
  "claimed",
  "abandoned",
  "superseded",
]);
export const LIFECYCLE_STATES = Object.freeze([
  "accepted",
  "locating",
  "reserved",
  "activating",
  "launching",
  "pairing-ready",
  "pairing",
  "open",
  "failed",
  "cancelled",
]);
export const LIFECYCLE_ACTIONS = Object.freeze([
  "retry",
  "confirm-takeover",
  "cancel",
  "choose-session",
  "choose-directory",
  "update-peer",
]);
export const LIFECYCLE_FAILURE_CODES = Object.freeze([
  "invalid-request",
  "unsupported",
  "operation-conflict",
  "operation-stale",
  "target-reserved",
  "writer-conflict",
  "controller-conflict",
  "capability-rejected",
  "project-not-found",
  "session-not-found",
  "directory-not-found",
  "ownership-unknown",
  "runtime-unavailable",
  "activation-failed",
  "identity-failed",
  "launch-failed",
  "launch-outcome-unknown",
  "launch-not-recoverable",
  "pairing-failed",
  "takeover-stale",
  "takeover-failed",
  "cancelled",
  "timeout",
  "internal-error",
]);

const now = () => Date.now();
const textEncoder = new TextEncoder();

/**
 * Build the type-agnostic part of an envelope. Identity (channelId/sessionId/senderId/senderName)
 * is stamped later by SecureChannel on send, so callers never pass it.
 */
const envelope = (eventType, eventSubtype, msg = {}) => ({ eventType, eventSubtype, msg, ts: now() });

function lifecycleEnvelope(eventSubtype, msg) {
  const result = envelope(EVENT_TYPE.CONTROL, eventSubtype, msg);
  if (textEncoder.encode(JSON.stringify(result)).byteLength > LIFECYCLE_MESSAGE_MAX_BYTES) {
    throw new RangeError("too-large");
  }
  return result;
}

const nonEmptyString = (value) => typeof value === "string" && value.trim() ? value.trim() : null;
const lifecycleRevision = (value) => Number.isSafeInteger(value) && value >= 0 ? value : 0;
const lifecycleState = (value) => LIFECYCLE_STATES.includes(value) ? value : "failed";
const lifecycleAction = (value) => LIFECYCLE_ACTIONS.includes(value) ? value : null;
const lifecycleFailureCode = (value) =>
  LIFECYCLE_FAILURE_CODES.includes(value) ? value : "internal-error";

function normalizeOpenTarget(target) {
  if (target?.kind === "existing") {
    return {
      kind: "existing",
      storeAuthority: nonEmptyString(target.storeAuthority),
      sessionId: nonEmptyString(target.sessionId) ?? "",
    };
  }
  return {
    kind: "new",
    projectName: nonEmptyString(target?.projectName) ?? "",
  };
}

export function normalizeOpenIntent(intent = {}) {
  const target = normalizeOpenTarget(intent.target);
  return {
    operationId: nonEmptyString(intent.operationId) ?? "",
    target,
    mode: intent.mode === "allow-all" ? "allow-all" : "default",
    name: target.kind === "new" ? nonEmptyString(intent.name) : null,
    takeoverRequested: target.kind === "existing" && intent.takeoverRequested === true,
  };
}

function canonicalJson(value) {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value).sort().map((key) =>
      `${JSON.stringify(key)}:${canonicalJson(value[key])}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

export async function openIntentFingerprint(intent) {
  const normalized = normalizeOpenIntent(intent?.msg ?? intent);
  const material = canonicalJson({
    target: normalized.target,
    mode: normalized.mode,
    name: normalized.name,
    takeoverRequested: normalized.takeoverRequested,
  });
  const digest = await globalThis.crypto.subtle.digest("SHA-256", textEncoder.encode(material));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function normalizeFailure(failure) {
  if (!failure || typeof failure !== "object") return null;
  return {
    code: lifecycleFailureCode(failure.code),
    actions: (Array.isArray(failure.actions) ? failure.actions : [])
      .map(lifecycleAction)
      .filter(Boolean),
    ...(typeof failure.message === "string" && failure.message ? { message: failure.message } : {}),
    ...(typeof failure.retryable === "boolean" ? { retryable: failure.retryable } : {}),
  };
}

function normalizeTakeoverChallenge(challenge, operationId, revision) {
  if (!challenge || typeof challenge !== "object") return null;
  const challengeId = nonEmptyString(challenge.challengeId);
  const sessionId = nonEmptyString(challenge.sessionId);
  const runtimeInstanceId = nonEmptyString(challenge.runtimeInstanceId);
  if (!challengeId || !sessionId || !runtimeInstanceId) return null;
  return {
    challengeId,
    operationId,
    revision,
    storeAuthority: nonEmptyString(challenge.storeAuthority),
    sessionId,
    runtimeInstanceId,
    generation: lifecycleRevision(challenge.generation),
    pid: Number.isSafeInteger(challenge.pid) && challenge.pid > 0 ? challenge.pid : null,
    processStartedAt: Number.isFinite(challenge.processStartedAt) ? challenge.processStartedAt : null,
    responsive: challenge.responsive === true,
    ...(typeof challenge.controllerName === "string" && challenge.controllerName
      ? { controllerName: challenge.controllerName }
      : {}),
  };
}

function normalizedLifecycleStatus(status = {}) {
  const operationId = nonEmptyString(status.operationId) ?? "";
  const revision = lifecycleRevision(status.revision);
  const target = status.target ? normalizeOpenTarget(status.target) : null;
  const failure = normalizeFailure(status.failure);
  const challenge = normalizeTakeoverChallenge(status.challenge, operationId, revision);
  return {
    operationId,
    fingerprint: nonEmptyString(status.fingerprint),
    state: lifecycleState(status.state),
    revision,
    ...(target ? { target } : {}),
    ...(status.action === "reconnect" || status.action === "activate" || status.action === "resume" ||
    status.action === "start" || status.action === "takeover" || status.action === "undecided"
      ? { action: status.action }
      : {}),
    ...(status.payload ? { payload: status.payload } : {}),
    ...(typeof status.name === "string" ? { name: status.name } : {}),
    ...(failure ? { failure } : {}),
    ...(challenge ? { challenge } : {}),
    ...(Number.isFinite(status.createdAt) ? { createdAt: status.createdAt } : {}),
    ...(Number.isFinite(status.updatedAt) ? { updatedAt: status.updatedAt } : {}),
  };
}

// ---- factories (ext -> phone : stream) -------------------------------------
export const assistantMessage = (content, messageId) =>
  envelope(EVENT_TYPE.STREAM, SUBTYPE.STREAM.ASSISTANT_MESSAGE, { content, messageId });
export const assistantDelta = (content, messageId) =>
  envelope(EVENT_TYPE.STREAM, SUBTYPE.STREAM.ASSISTANT_DELTA, { content, messageId });
export const intent = (text, thinking = false) =>
  envelope(EVENT_TYPE.STREAM, SUBTYPE.STREAM.INTENT, { text: text ?? null, thinking: thinking === true });
export const toolStart = (toolCallId, toolName, args) =>
  envelope(EVENT_TYPE.STREAM, SUBTYPE.STREAM.TOOL_START, { toolCallId, toolName, args });
export const toolComplete = (toolCallId, toolName, success, resultPreview) =>
  envelope(EVENT_TYPE.STREAM, SUBTYPE.STREAM.TOOL_COMPLETE, {
    toolCallId,
    toolName,
    success,
    resultPreview,
  });
export const logLine = (level, message) =>
  envelope(EVENT_TYPE.STREAM, SUBTYPE.STREAM.LOG, { level, message });
/**
 * Turn-level activity (ext -> phone). `busy` is true when the agent starts a turn and false when
 * its processing loop goes idle. Drives the phone's Stop control for the whole abortable turn.
 */
export const activity = (busy) =>
  envelope(EVENT_TYPE.STREAM, SUBTYPE.STREAM.ACTIVITY, { busy: Boolean(busy) });
/**
 * A user prompt echoed from the laptop to the phone. `origin` records which device typed it
 * ('phone' = this device; 'terminal' = the laptop). `id` is a stable id (the SDK event id) for dedup.
 */
export const userMessage = (text, origin = "terminal", id) =>
  envelope(EVENT_TYPE.STREAM, SUBTYPE.STREAM.USER_MESSAGE, { text, origin, id });

// ---- factories (phone -> ext : prompt) -------------------------------------
/**
 * A phone-typed prompt relayed to the laptop session. `attachments` (optional) carries inline
 * images the user picked: each is `{ data (base64, no `data:` prefix), mimeType, name }`.
 * `delivery` is omitted for the existing immediate/steering behavior and stamped only for enqueue.
 */
export const prompt = (text, attachments = null, delivery = "immediate") =>
  envelope(EVENT_TYPE.PROMPT, SUBTYPE.PROMPT.PROMPT, {
    text,
    ...(Array.isArray(attachments) && attachments.length ? { attachments } : {}),
    ...(delivery === "enqueue" ? { delivery } : {}),
  });

// ---- factories (approval / decision) ---------------------------------------
/**
 * Approval request mirrors the NATIVE Copilot permission prompt. `options` is the verbatim set of
 * choices the terminal would show; the phone echoes back the chosen option id in the decision.
 */
export const approvalRequest = (requestId, toolName, toolArgs, options, deadline = {}) =>
  envelope(EVENT_TYPE.APPROVAL, SUBTYPE.APPROVAL.REQUEST, {
    requestId,
    toolName,
    toolArgs,
    options,
    ...(deadline && typeof deadline === "object" && "timeoutMs" in deadline
      ? { timeoutMs: deadline.timeoutMs }
      : {}),
    ...(deadline && typeof deadline === "object" && "expiresAt" in deadline
      ? { expiresAt: deadline.expiresAt }
      : {}),
  });
export const approvalDecision = (requestId, optionId, raw) =>
  envelope(EVENT_TYPE.DECISION, SUBTYPE.DECISION.APPROVAL_DECISION, { requestId, optionId, raw });
/**
 * Ext -> phone notice that an approval was resolved elsewhere (decided on another device or the
 * relay stopped); dismiss any open banner for it. The approval analogue of elicitationComplete
 * — permissions have no native completion event, so the relay synthesizes this on resolve. `decision`
 * is informational (the chosen optionId, "timeout", ...); the phone only needs the requestId.
 */
export const approvalComplete = (requestId, decision) =>
  envelope(EVENT_TYPE.APPROVAL, SUBTYPE.APPROVAL.COMPLETE, { requestId, decision });

// ---- factories (elicitation / ask_user) ------------------------------------
/**
 * Elicitation request mirrors the CLI's `ask_user` prompt (ext -> phone). `mode` is "form" or "url".
 * `requestedSchema` is the JSON Schema the phone renders as a form.
 */
export const elicitationRequest = (requestId, message, mode, requestedSchema, toolCallId, url) =>
  envelope(EVENT_TYPE.ELICITATION, SUBTYPE.ELICITATION.REQUEST, {
    requestId,
    message,
    mode: mode ?? "form",
    requestedSchema,
    toolCallId,
    url,
  });
/**
 * Phone -> ext answer to an elicitation. `action` is "accept"/"decline"/"cancel". `content` (only
 * meaningful for "accept") carries the submitted field values; never shipped when the user backs out.
 */
export const elicitationResponse = (requestId, action, content) =>
  envelope(EVENT_TYPE.ELICITATION_RESPONSE, SUBTYPE.ELICITATION_RESPONSE.RESPONSE, {
    requestId,
    action,
    content: action === "accept" ? content : undefined,
  });
/** Ext -> phone notice that an elicitation was resolved elsewhere; dismiss any open form for it. */
export const elicitationComplete = (requestId, action) =>
  envelope(EVENT_TYPE.ELICITATION, SUBTYPE.ELICITATION.COMPLETE, { requestId, action });

// ---- factories (control) ---------------------------------------------------
/**
 * Ext -> phone: the channel is live. channelId/sessionId now travel on the ENVELOPE (stamped by the
 * channel), so this only carries the session's cwd + CLI chat title (summary).
 */
export const channelUp = (cwd, title, capabilities) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.CHANNEL_UP, {
    cwd, title, ...(capabilities ? { capabilities } : {}),
  });
/** Lightweight, post-start metadata refresh (ext -> phone): the latest CLI title (and cwd). */
export const sessionMeta = (title, cwd) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.SESSION_META, { title, cwd });
export const channelDown = (reason) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.CHANNEL_DOWN, { reason });
/**
 * Ext -> phone liveness beat. `latestTurnIndex` is the highest committed turn_index (a fresh forward
 * cursor), or null when unknown. `busy` re-asserts the turn-in-flight flag each beat; null = unknown.
 */
export const heartbeat = (latestTurnIndex = null, busy = null) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.HEARTBEAT, { latestTurnIndex, busy });
export const modeChange = (mode) => envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.MODE, { mode });
/** Phone -> ext request to stop the current turn (best-effort; safe when nothing is running). */
export const interrupt = () => envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.INTERRUPT, {});

// ---- factories (history backfill) ------------------------------------------
/**
 * Phone -> ext request for a page of turns. `before` = backward cursor (older, scrollback);
 * `since` = forward cursor (newer, catch-up); both null = latest page. Mutually exclusive.
 */
export const historyRequest = (before = null, limit, since = null) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.HISTORY_REQUEST, { before, since, limit });
/**
 * Ext -> phone page of history. `items` ascending; `nextCursor`/`hasMore` continue the SAME
 * direction; `since` echoes the request's forward cursor (non-null => forward catch-up page).
 */
export const history = (items, nextCursor = null, hasMore = false, since = null) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.HISTORY, { items, nextCursor, hasMore, since });

// ---- factories (recent-turns snapshot) -------------------------------------
/**
 * Phone -> ext request for the extension's in-memory recent-turns buffer (the last `limit` turns it
 * knows). Preferred over history_request for the connect-time backfill because the buffer keeps the
 * FULL assistant text the CLI store drops for long/multi-tool turns.
 */
export const recentTurnsRequest = (limit) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.RECENT_TURNS_REQUEST, { limit });
/**
 * Ext -> phone snapshot of the last N turns as flat message entries, ascending (chronological).
 * Each item is `{ role, text, ts, id }`; `id` is a stable per-message id (assistant messageId /
 * user event id / `seed-<turn>-<role>` for store-seeded turns) so re-applying a snapshot is a no-op.
 * Self-contained (no cursor): the phone merges it into the transcript tail by id + content dedup.
 */
export const recentTurns = (items) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.RECENT_TURNS, { items: Array.isArray(items) ? items : [] });

// ---- factories (state snapshot) --------------------------------------------
/** Phone -> ext: request the current session state on (re)connect / refresh / resume. */
export const stateRequest = () => envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.STATE_REQUEST, {});
/**
 * Ext -> phone snapshot of the live session state. `approvals`/`elicitations` are the still-pending
 * prompt PAYLOADS (flat, same shape as an approvalRequest/elicitationRequest `msg`) so the phone
 * reuses its normal renderers.
 */
export const stateSnapshot = ({
  busy = false,
  abortable = false,
  mode = null,
  latestTurnIndex = null,
  approvals = [],
  elicitations = [],
  capabilities,
} = {}) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.STATE_SNAPSHOT, {
    busy: Boolean(busy),
    abortable: Boolean(abortable),
    mode,
    latestTurnIndex,
    approvals,
    elicitations,
    ...(capabilities ? { capabilities } : {}),
  });

// ---- factories (phone-launched sessions #156, all ride CONTROL) ------------
/** Phone -> listener: request the machine's registered projects (sent right after pairing). */
export const projectListRequest = () =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.PROJECT_LIST_REQUEST, {});
/**
 * Listener -> phone: the machine's registered projects + the listener's display name. `projects`
 * is `[{ name, path, isDefault }]` (path is informational for the phone; selection is by name).
 * `deviceId` (optional) is a STABLE, NON-SECRET id the listener persists across `weft start`
 * restarts (see extension/src/deviceIdentity.mjs) so the phone can recognize "same laptop" even
 * though its ephemeral pairing `channelId`/keypair are freshly minted every run (by design, for
 * forward secrecy — see docs/pairing.md). Never derived from or tied to any cryptographic key.
 */
export const projectList = (
  projects,
  deviceName,
  deviceId,
  capabilities = null,
  defaultPermissionMode = "default",
) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.PROJECT_LIST, {
    projects: Array.isArray(projects) ? projects : [],
    deviceName: deviceName ?? null,
    deviceId: deviceId ?? null,
    ...(Array.isArray(capabilities)
      ? { capabilities: capabilities.filter((value) => typeof value === "string" && value) }
      : {}),
    defaultPermissionMode: defaultPermissionMode === "allow-all" ? "allow-all" : "default",
  });
/**
 * Phone -> listener: spawn a new Copilot session. `requestId` correlates the reply; `projectName`
 * selects a registered project; `mode` is "default" | "allow-all"; `name` is the friendly session name.
 */
export const spawnSession = (requestId, projectName, mode = "default", name = null) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.SPAWN_SESSION, { requestId, projectName, mode, name });
/** Phone -> listener: request the machine's recent resumable CLI sessions (on-demand; not pushed on
 *  bind). Optional `limit` is clamped by the listener to SESSION_LIST_MAX. Optional `cwd` narrows the
 *  query to one folder *before* the cap applies — without it a busy folder's history is invisible
 *  behind whatever happens to be newest across the whole machine. */
export const sessionListRequest = (limit = null, cwd = null) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.SESSION_LIST_REQUEST, {
    ...(Number.isFinite(limit) && limit > 0 ? { limit: Math.floor(limit) } : {}),
    ...(typeof cwd === "string" && cwd ? { cwd } : {}),
  });
/**
 * Listener -> phone: the machine's recent resumable sessions, newest-first. Each entry is
 * `{ sessionId, title, cwd, repository, branch, updatedAt }` — `sessionId` is the CLI session UUID
 * the phone passes back to resumeSession() (and dedupes live cards on); `title` is the CLI-derived
 * chat summary (falling back to the cwd basename); `repository`/`branch` are null when unknown;
 * `updatedAt` is epoch ms of the session's last activity (for "2h ago" sorting/labels). Malformed
 * entries (missing sessionId/cwd) are dropped so a corrupt row can never wedge the phone list.
 */
export const sessionList = (sessions, folders = null) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.SESSION_LIST, {
    sessions: (Array.isArray(sessions) ? sessions : [])
      .filter((s) => s && typeof s.sessionId === "string" && s.sessionId && typeof s.cwd === "string" && s.cwd)
      .map((s) => ({
        sessionId: s.sessionId,
        title: typeof s.title === "string" && s.title.length > 0 ? s.title : null,
        cwd: s.cwd,
        repository: typeof s.repository === "string" && s.repository.length > 0 ? s.repository : null,
        branch: typeof s.branch === "string" && s.branch.length > 0 ? s.branch : null,
        updatedAt: Number.isFinite(s.updatedAt) ? s.updatedAt : null,
        ...(s.running === true ? { running: true } : {}),
      })),
    // Whole-store per-folder totals, independent of the capped page above, so the phone's folder
    // picker can say how many sessions a folder really holds rather than how many of them happened
    // to fit in this reply. Omitted entirely by listeners that predate it.
    ...(Array.isArray(folders)
      ? {
          folders: folders
            .filter((f) => f && typeof f.cwd === "string" && f.cwd)
            .map((f) => ({
              cwd: f.cwd,
              count: Number.isFinite(f.count) ? f.count : 0,
              updatedAt: Number.isFinite(f.updatedAt) ? f.updatedAt : null,
            })),
        }
      : {}),
  });
/**
 * Phone -> listener: resume an existing CLI session. `requestId` correlates the reply (reusing the
 * SPAWN_PAIRING / SPAWN_RESULT path); `sessionId` is the CLI session UUID from sessionList(); `mode`
 * is "default" | "allow-all" (the resumed session's permission mode).
 *
 * `force` is the user's second tap. The listener refuses to resume a session that is already
 * attached to a phone and healthy, because doing so forks a second CLI process onto one session
 * store entry. But the phone cannot always tell a healthy attachment from a wedged one, and if weft
 * on the laptop has broken then resume is the ONLY way back — so `force` lets the user override the
 * refusal, after being told what it will cost (the running session is closed first, not left to run
 * alongside).
 */
export const resumeSession = (requestId, sessionId, mode = "default", force = false) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.RESUME_SESSION, {
    requestId,
    sessionId: typeof sessionId === "string" ? sessionId : "",
    mode,
    force: Boolean(force),
  });

/** Phone -> listener: request one generic, idempotent session open operation. */
export const openSession = (operationId, target, options = {}) =>
  lifecycleEnvelope(
    SUBTYPE.CONTROL.OPEN_SESSION,
    normalizeOpenIntent({ operationId, target, ...options }),
  );

/** Phone -> listener: inspect one previously accepted lifecycle operation. */
export const lifecycleStatusRequest = (operationId) =>
  lifecycleEnvelope(SUBTYPE.CONTROL.LIFECYCLE_STATUS_REQUEST, {
    operationId: nonEmptyString(operationId) ?? "",
  });

/** Listener -> phone: a structured, revisioned lifecycle operation snapshot. */
export const lifecycleStatus = (status) =>
  lifecycleEnvelope(SUBTYPE.CONTROL.LIFECYCLE_STATUS, normalizedLifecycleStatus(status));

/** Phone -> listener: confirm the exact takeover challenge and operation revision shown to the user. */
export const takeoverConfirm = (operationId, challengeId, revision) =>
  lifecycleEnvelope(SUBTYPE.CONTROL.TAKEOVER_CONFIRM, {
    operationId: nonEmptyString(operationId) ?? "",
    challengeId: nonEmptyString(challengeId) ?? "",
    revision: lifecycleRevision(revision),
  });

/** Phone -> listener: cancel an operation if the supplied revision is still current. */
export const lifecycleCancel = (operationId, revision = null) =>
  lifecycleEnvelope(SUBTYPE.CONTROL.LIFECYCLE_CANCEL, {
    operationId: nonEmptyString(operationId) ?? "",
    ...(Number.isSafeInteger(revision) && revision >= 0 ? { revision } : {}),
  });

/** Convert a supported legacy Start/Resume envelope to the generic Open contract. */
export function openSessionFromLegacy(message) {
  if (message?.eventType !== EVENT_TYPE.CONTROL || !message.msg) return null;
  if (message.eventSubtype === SUBTYPE.CONTROL.SPAWN_SESSION) {
    return openSession(message.msg.requestId, {
      kind: "new",
      projectName: message.msg.projectName,
    }, {
      mode: message.msg.mode,
      name: message.msg.name,
    });
  }
  if (message.eventSubtype === SUBTYPE.CONTROL.RESUME_SESSION) {
    return openSession(message.msg.requestId, {
      kind: "existing",
      storeAuthority: null,
      sessionId: message.msg.sessionId,
    }, {
      mode: message.msg.mode,
      takeoverRequested: message.msg.force === true,
    });
  }
  return null;
}

/** Project a generic Open envelope to a legacy Start or Resume envelope for an older Station. */
export function openSessionToLegacy(message) {
  const intent = normalizeOpenIntent(message?.msg ?? message);
  if (!intent.operationId) return null;
  return intent.target.kind === "existing"
    ? resumeSession(
      intent.operationId,
      intent.target.sessionId,
      intent.mode,
      intent.takeoverRequested,
    )
    : spawnSession(
      intent.operationId,
      intent.target.projectName,
      intent.mode,
      intent.name,
    );
}

const LEGACY_TO_LIFECYCLE_STATE = Object.freeze({
  accepted: "accepted",
  launched: "launching",
  ready: "pairing-ready",
  claimed: "open",
  failed: "failed",
  abandoned: "cancelled",
  superseded: "cancelled",
});
const LIFECYCLE_TO_LEGACY_STATE = Object.freeze({
  accepted: "accepted",
  locating: "accepted",
  reserved: "accepted",
  activating: "launched",
  launching: "launched",
  "pairing-ready": "ready",
  pairing: "ready",
  open: "claimed",
  failed: "failed",
  cancelled: "abandoned",
});

/** Convert one supported legacy launch reply to a structured lifecycle snapshot. */
export function lifecycleStatusFromLegacy(message, previous = null) {
  if (message?.eventType !== EVENT_TYPE.CONTROL || !message.msg) return null;
  const operationId = nonEmptyString(message.msg.requestId);
  if (!operationId) return null;
  const previousStatus = previous?.msg ?? previous;
  const revision = previousStatus?.operationId === operationId
    ? lifecycleRevision(previousStatus.revision) + 1
    : 0;
  if (message.eventSubtype === SUBTYPE.CONTROL.LAUNCH_STATUS) {
    const operation = message.msg.operation;
    const target = operation === "resume"
      ? { kind: "existing", storeAuthority: null, sessionId: message.msg.sessionId }
      : operation === "new"
        ? { kind: "new", projectName: message.msg.projectName }
        : null;
    return lifecycleStatus({
      operationId,
      revision,
      state: LEGACY_TO_LIFECYCLE_STATE[message.msg.state] ?? "failed",
      ...(target ? { target } : {}),
      ...(message.msg.payload ? { payload: message.msg.payload } : {}),
      ...(typeof message.msg.name === "string" ? { name: message.msg.name } : {}),
      ...(typeof message.msg.error === "string" && message.msg.error
        ? {
            failure: {
              code: message.msg.state === "abandoned" ? "cancelled" : "launch-failed",
              message: message.msg.error,
              actions: message.msg.state === "failed" ? ["retry"] : [],
            },
          }
        : {}),
      createdAt: message.msg.createdAt,
      updatedAt: message.msg.readyAt ?? message.msg.launchedAt ?? message.msg.createdAt,
    });
  }
  if (message.eventSubtype === SUBTYPE.CONTROL.SPAWN_PAIRING) {
    return lifecycleStatus({
      operationId,
      revision,
      state: "pairing-ready",
      payload: message.msg.payload,
      name: message.msg.name,
      target: message.msg.projectName
        ? { kind: "new", projectName: message.msg.projectName }
        : undefined,
    });
  }
  if (message.eventSubtype === SUBTYPE.CONTROL.SPAWN_RESULT) {
    return lifecycleStatus({
      operationId,
      revision,
      state: message.msg.ok ? "open" : "failed",
      ...(!message.msg.ok
        ? {
            failure: {
              code: "launch-failed",
              ...(typeof message.msg.error === "string" && message.msg.error
                ? { message: message.msg.error }
                : {}),
              actions: ["retry"],
            },
          }
        : {}),
    });
  }
  if (message.eventSubtype === SUBTYPE.CONTROL.SESSION_CLAIMED) {
    return lifecycleStatus({ operationId, revision, state: "open" });
  }
  return null;
}

/** Project a lifecycle snapshot to the legacy replies understood by older phones. */
export function lifecycleStatusToLegacy(message) {
  const status = normalizedLifecycleStatus(message?.msg ?? message);
  if (!status.operationId) return [];
  const target = status.target;
  const operation = target?.kind === "existing" ? "resume" : target?.kind === "new" ? "new" : undefined;
  const details = {
    ...(operation ? { operation } : {}),
    ...(target?.kind === "existing" ? { sessionId: target.sessionId } : {}),
    ...(target?.kind === "new" ? { projectName: target.projectName } : {}),
    ...(typeof status.name === "string" ? { name: status.name } : {}),
    ...(status.payload ? { payload: status.payload } : {}),
    ...(status.failure?.message ? { error: status.failure.message } : {}),
    ...(Number.isFinite(status.createdAt) ? { createdAt: status.createdAt } : {}),
    ...(Number.isFinite(status.updatedAt) ? { readyAt: status.updatedAt } : {}),
  };
  const projected = [
    launchStatus(
      status.operationId,
      LIFECYCLE_TO_LEGACY_STATE[status.state] ?? "failed",
      details,
    ),
  ];
  if (
    status.payload &&
    (
      status.state === "activating" ||
      status.state === "launching" ||
      status.state === "pairing-ready" ||
      status.state === "pairing"
    )
  ) {
    projected.push(spawnPairing(
      status.operationId,
      status.payload,
      status.name ?? null,
      target?.kind === "new" ? target.projectName : null,
    ));
  }
  if (status.state === "failed") {
    projected.push(spawnResult(status.operationId, false, status.failure?.message ?? status.failure?.code ?? null));
  } else if (
    status.state === "launching" ||
    status.state === "pairing-ready" ||
    status.state === "pairing" ||
    status.state === "open"
  ) {
    projected.push(spawnResult(status.operationId, true));
  }
  return projected;
}

/** Keep the latest snapshot for one operation; stale or duplicate revisions never replace it. */
export function latestLifecycleStatus(current, candidate) {
  const currentStatus = current?.msg ?? current;
  const candidateStatus = candidate?.msg ?? candidate;
  if (!candidateStatus || typeof candidateStatus !== "object") return current;
  if (!currentStatus || typeof currentStatus !== "object") return candidate;
  if (currentStatus.operationId !== candidateStatus.operationId) return candidate;
  return lifecycleRevision(candidateStatus.revision) > lifecycleRevision(currentStatus.revision)
    ? candidate
    : current;
}
/**
 * Listener -> phone: the pre-minted pairing payload of a freshly spawned session, so the phone
 * pairs to it digitally (no QR). `payload` is a buildPairingPayload() result; `name`/`projectName`
 * let the phone label the Initializing card immediately.
 */
export const spawnPairing = (requestId, payload, name, projectName) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.SPAWN_PAIRING, { requestId, payload, name, projectName });
/** Listener -> phone: terminal result of a spawn request. `ok=false` carries a human `error`. */
export const spawnResult = (requestId, ok, error = null) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.SPAWN_RESULT, { requestId, ok: Boolean(ok), error });
/** Listener -> phone: durable launch lifecycle. Private handoff key material is never included. */
export const launchStatus = (requestId, state, details = {}) => {
  const safe = details && typeof details === "object" ? details : {};
  return envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.LAUNCH_STATUS, {
    requestId: typeof requestId === "string" ? requestId : "",
    state: LAUNCH_STATES.includes(state) ? state : "failed",
    ...(safe.payload ? { payload: safe.payload } : {}),
    ...(safe.operation === "new" || safe.operation === "resume" ? { operation: safe.operation } : {}),
    ...(typeof safe.projectName === "string" ? { projectName: safe.projectName } : {}),
    ...(typeof safe.sessionId === "string" ? { sessionId: safe.sessionId } : {}),
    ...(typeof safe.name === "string" ? { name: safe.name } : {}),
    ...(typeof safe.error === "string" ? { error: safe.error } : {}),
    ...(Number.isFinite(safe.createdAt) ? { createdAt: safe.createdAt } : {}),
    ...(Number.isFinite(safe.launchedAt) ? { launchedAt: safe.launchedAt } : {}),
    ...(Number.isFinite(safe.readyAt) ? { readyAt: safe.readyAt } : {}),
    ...(Number.isInteger(safe.pid) && safe.pid > 0 ? { pid: safe.pid } : {}),
  });
};
/** Phone -> listener: forget this device; the listener stops (or drops its phone binding). */
export const forgetDevice = () =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.FORGET_DEVICE, {});
/**
 * Listener -> phone: proactive liveness beat for the DEVICE channel (distinct from the per-session
 * heartbeat()). Sent on an interval whether or not the phone has asked for anything, so a hung or
 * crashed `weft` process shows as offline even if the underlying transport still reports
 * "connected". `deviceId` is the same stable, non-secret id carried in projectList().
 */
export const deviceHeartbeat = (deviceId = null) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.DEVICE_HEARTBEAT, { deviceId: deviceId ?? null });

// Terminal payloads remain private to the terminal consumers, not the diagnostic event log.
// Receivers validate action-specific fields before any PTY or rendering operation.
export const terminalRequest = (request) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.TERMINAL_REQUEST, { ...request });
export const terminalState = (state) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.TERMINAL_STATE, { ...state });
export const terminalOutput = (output) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.TERMINAL_OUTPUT, { ...output });
export const terminalSnapshot = (snapshot) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.TERMINAL_SNAPSHOT, { ...snapshot });

export const deviceMonitorStart = (monitorId, intervalMs = null, leaseMs = null) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.DEVICE_MONITOR_START, {
    monitorId: typeof monitorId === "string" ? monitorId : "",
    ...(Number.isFinite(intervalMs) && intervalMs > 0 ? { intervalMs: Math.floor(intervalMs) } : {}),
    ...(Number.isFinite(leaseMs) && leaseMs > 0 ? { leaseMs: Math.floor(leaseMs) } : {}),
  });

export const deviceMonitorStop = (monitorId) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.DEVICE_MONITOR_STOP, {
    monitorId: typeof monitorId === "string" ? monitorId : "",
  });

const requestIdOrEmpty = (value) => typeof value === "string" ? value : "";
const utilityCode = (code) => DEVICE_UTILITY_CODES.includes(code) ? code : "unavailable";

export const clipboardRead = (requestId) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.CLIPBOARD_READ, { requestId: requestIdOrEmpty(requestId) });

export const clipboardWrite = (requestId, text) => {
  const error = clipboardTextError(text);
  if (error) throw new RangeError(error);
  return envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.CLIPBOARD_WRITE, {
    requestId: requestIdOrEmpty(requestId), text,
  });
};

export const clipboardResult = (requestId, operation, code, text) => {
  let safeCode = utilityCode(code);
  const safeOperation = operation === "read" ? "read" : "write";
  if (safeCode === "ok" && safeOperation === "read") safeCode = clipboardTextError(text) ?? "ok";
  return envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.CLIPBOARD_RESULT, {
    requestId: requestIdOrEmpty(requestId),
    operation: safeOperation,
    code: safeCode,
    ...(safeCode === "ok" && safeOperation === "read" ? { text } : {}),
  });
};

export const keepAwakeStart = (requestId, leaseId, durationMs) => {
  const duration = normalizeKeepAwakeDuration(durationMs);
  if (duration === null) throw new RangeError("invalid-request");
  return envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.KEEP_AWAKE_START, {
    requestId: requestIdOrEmpty(requestId), leaseId: requestIdOrEmpty(leaseId), durationMs: duration,
  });
};
export const keepAwakeStop = (requestId, leaseId) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.KEEP_AWAKE_STOP, {
    requestId: requestIdOrEmpty(requestId), leaseId: requestIdOrEmpty(leaseId),
  });
export const keepAwakeStatusRequest = (requestId) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.KEEP_AWAKE_STATUS_REQUEST, {
    requestId: requestIdOrEmpty(requestId),
  });
export const keepAwakeStatus = (requestId, { leaseId = null, active = false, expiresAt = null, revision = 0, code = "ok" } = {}) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.KEEP_AWAKE_STATUS, {
    requestId: typeof requestId === "string" ? requestId : null,
    leaseId: typeof leaseId === "string" ? leaseId : null,
    active: active === true && typeof leaseId === "string" && Number.isFinite(expiresAt),
    expiresAt: active === true && typeof leaseId === "string" && Number.isFinite(expiresAt) ? expiresAt : null,
    revision: Number.isSafeInteger(revision) && revision >= 0 ? revision : 0,
    code: utilityCode(code),
  });

export const deviceSnapshot = ({
  monitorId,
  sequence,
  capturedAt,
  effectiveIntervalMs,
  leaseExpiresAt,
  system = {},
  apps,
  observedAt = {},
  issues,
} = {}) => {
  const numberOrNull = (value) => (Number.isFinite(value) ? value : null);
  return envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.DEVICE_SNAPSHOT, {
    schemaVersion: 1,
    monitorId: typeof monitorId === "string" ? monitorId : "",
    sequence: Number.isInteger(sequence) && sequence >= 0 ? sequence : 0,
    capturedAt: Number.isFinite(capturedAt) ? capturedAt : now(),
    effectiveIntervalMs: Number.isFinite(effectiveIntervalMs) ? effectiveIntervalMs : null,
    leaseExpiresAt: Number.isFinite(leaseExpiresAt) ? leaseExpiresAt : null,
    system: {
      cpuPercent: numberOrNull(system.cpuPercent),
      memoryUsedBytes: numberOrNull(system.memoryUsedBytes),
      memoryTotalBytes: numberOrNull(system.memoryTotalBytes),
      uptimeSeconds: numberOrNull(system.uptimeSeconds),
      diskUsedBytes: numberOrNull(system.diskUsedBytes),
      diskTotalBytes: numberOrNull(system.diskTotalBytes),
      batteryPercent: numberOrNull(system.batteryPercent),
      batteryCharging: typeof system.batteryCharging === "boolean" ? system.batteryCharging : null,
      onAcPower: typeof system.onAcPower === "boolean" ? system.onAcPower : null,
    },
    apps: (Array.isArray(apps) ? apps : []).filter(
      (app) => app && typeof app.id === "string" && app.id && typeof app.name === "string" && app.name,
    ),
    observedAt: {
      system: numberOrNull(observedAt.system),
      disk: numberOrNull(observedAt.disk),
      battery: numberOrNull(observedAt.battery),
      apps: numberOrNull(observedAt.apps),
    },
    issues: (Array.isArray(issues) ? issues : []).filter(
      (value) =>
        value &&
        ["system", "disk", "battery", "apps"].includes(value.component) &&
        ["unavailable", "timeout"].includes(value.code),
    ),
  });
};

/**
 * Phone -> ext: Voice Mode is now on/off (#176). While on, the extension prepends a short
 * spoken-response directive to each relayed phone prompt so the agent replies for LISTENING
 * (short, conversational, summarize code) instead of dense on-screen text.
 */
export const voiceMode = (active) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.VOICE_MODE, { active: Boolean(active) });

/**
 * Phone -> ext: run a whitelisted Copilot CLI slash command on the laptop session. `name` is the
 * command (no leading slash, e.g. "rename"); `input` is the optional free-text argument. The
 * extension re-validates `name` against the commands.mjs whitelist before invoking.
 */
export const invokeCommand = (name, input) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.INVOKE_COMMAND, {
    name: typeof name === "string" ? name : "",
    ...(typeof input === "string" && input.length > 0 ? { input } : {}),
  });

/**
 * Listener -> phone: the in-session `/weft` sessions currently waiting to be adopted by the paired
 * phone. Each offer is `{ channelId, name, cwd, payload }`, where `payload` is a buildPairingPayload()
 * result for that session's own SESSION channel — so the phone pairs to it digitally (no QR), the
 * same way it consumes spawnPairing(). `channelId` (also present inside `payload`) is the stable key
 * the phone dedupes on and echoes back in sessionClaimed(). Non-`{channelId, payload}` entries are
 * dropped so a malformed offer can never wedge the phone.
 */
export const sessionOffers = (offers) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.SESSION_OFFERS, {
    offers: (Array.isArray(offers) ? offers : [])
      .filter((o) => o && typeof o.channelId === "string" && o.payload)
      .map((o) => ({
        channelId: o.channelId,
        name: typeof o.name === "string" && o.name.length > 0 ? o.name : null,
        cwd: typeof o.cwd === "string" && o.cwd.length > 0 ? o.cwd : null,
        payload: o.payload,
      })),
  });

/**
 * Phone -> listener: the phone has adopted (paired to) an offered session, so the station drops it
 * from its advertised set and stops re-offering it. Idempotent — a claim for an unknown/already-
 * removed channelId is a no-op on the station. `channelId` is the offered session's channel.
 */
export const sessionClaimed = (channelId, requestId = null) =>
  envelope(EVENT_TYPE.CONTROL, SUBTYPE.CONTROL.SESSION_CLAIMED, {
    channelId: typeof channelId === "string" ? channelId : "",
    ...(typeof requestId === "string" && requestId ? { requestId } : {}),
  });

/** Minimal structural validation of a decrypted envelope (kept dependency-free). */
export function isValidEnvelope(env) {
  return (
    env != null &&
    typeof env === "object" &&
    typeof env.eventType === "string" &&
    typeof env.eventSubtype === "string" &&
    typeof env.ts === "number" &&
    env.msg != null &&
    typeof env.msg === "object"
  );
}
